import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import type { AgentApiJobSourcesService } from '@/types/serializers';
import useFetchJobSourcesServices from '@/features/agent/linked_accounts/api/useFetchJobSourcesServices';
import TrialAccountLinkStep1 from '../components/TrialAccountLinkStep1';
import TrialAccountLinkStep2 from '../components/TrialAccountLinkStep2';
import TrialAccountLinkStep3 from '../components/TrialAccountLinkStep3';
import { MdKeyboardArrowLeft } from 'react-icons/md';

export default function TrialAccountLinkTemplate() {
  const navigate = useNavigate();
  const { data: services } = useFetchJobSourcesServices();
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [selectedServiceName, setSelectedServiceName] = useState<string | null>(null);
  const [selectedService, setSelectedService] = useState<AgentApiJobSourcesService | null>(null);
  const [isStep3Loading, setIsStep3Loading] = useState(false);

  const handleServiceSelect = (service: { name: string; sourceType: string }) => {
    setSelectedServiceName(service.name);

    // 選択されたサービスに関連するサービスを取得
    const relatedServices = services?.filter(
      (s) => s.name.includes(service.name) || service.name.includes(s.name)
    );

    // 関連サービスが1つだけの場合は、直接ステップ3へ
    if (relatedServices && relatedServices.length === 1) {
      setSelectedService(relatedServices[0]);
      setStep(3);
    } else {
      // 複数の認証方法がある場合は、ステップ2へ
      setStep(2);
    }
  };

  const handleAuthMethodSelect = (service: AgentApiJobSourcesService) => {
    setSelectedService(service);
    setStep(3);
  };

  const handleBack = () => {
    if (step === 3) {
      // ステップ2があった場合は戻る、なければステップ1へ
      const relatedServices = services?.filter(
        (s) =>
          selectedServiceName &&
          (s.name.includes(selectedServiceName) || selectedServiceName.includes(s.name))
      );
      if (relatedServices && relatedServices.length > 1) {
        setStep(2);
        setSelectedService(null);
      } else {
        setStep(1);
        setSelectedServiceName(null);
        setSelectedService(null);
      }
    } else if (step === 2) {
      setStep(1);
      setSelectedServiceName(null);
    } else {
      navigate({ to: '/agent' });
    }
  };

  const handleComplete = () => {
    navigate({ to: '/agent/provided_job_posts' });
  };

  return (
    <div className="relative">
      {/* 戻るボタン（ステップ2以降、かつローディング中でない場合のみ表示） */}
      {step > 1 && !isStep3Loading && (
        <button
          type="button"
          onClick={handleBack}
          className="absolute top-[45px] left-[45px] z-20 flex size-10 items-center justify-center"
        >
          <MdKeyboardArrowLeft className="size-xxl text-placeholder" />
        </button>
      )}

      {/* コンテンツ */}
      <div className="relative z-10 flex flex-1 items-center justify-center px-[250px] py-[153px]">
        {step === 1 && <TrialAccountLinkStep1 onServiceSelect={handleServiceSelect} />}
        {step === 2 && selectedServiceName && (
          <TrialAccountLinkStep2
            selectedServiceName={selectedServiceName}
            onAuthMethodSelect={handleAuthMethodSelect}
          />
        )}
        {step === 3 && selectedService && (
          <TrialAccountLinkStep3
            service={selectedService}
            onComplete={handleComplete}
            onLoadingChange={setIsStep3Loading}
          />
        )}
      </div>
    </div>
  );
}
